// src/utils/reportGenerators.js
import { generateRequirementsSummary } from '../services/openAiService';

// Base styles definition
const baseStyles = `
    /* Base typography and variables */
    :root {
        --font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        --primary-text: #1f2937;
        --secondary-text: #4b5563;
        --accent-blue: #1a56db;
        --border-color: #e5e7eb;
        --background-subtle: #f9fafb;
        
        /* Status colors */
        --required-bg: #dcfce7;
        --required-text: #166534;
        --nice-bg: #dbeafe;
        --nice-text: #1e40af;
        --not-needed-bg: #f3f4f6;
        --not-needed-text: #374151;
        --need-info-bg: #fef3c7;
        --need-info-text: #92400e;
        --warning-bg: #fee2e2;
        --warning-text: #991b1b;
    }

    body { 
        font-family: var(--font-family);
        font-size: 11pt;
        line-height: 1.4;
        color: var(--primary-text);
        margin: 0;
        padding: 2rem;
        max-width: 100%;
    }

    /* Headers with optimized margins */
    .header { 
        margin-bottom: 1.5rem;
        border-bottom: 2px solid var(--border-color);
        padding-bottom: 1rem;
    }

    .title { 
        font-size: 24pt;
        font-weight: bold;
        color: var(--accent-blue);
        margin-bottom: 0.5rem;
    }

    .date { 
        color: var(--secondary-text);
        font-size: 0.9rem;
    }

    /* Sections with optimized spacing */
    .section { 
        margin-bottom: 2rem;
        break-inside: avoid;
    }

    .section-title { 
        font-size: 14pt;
        font-weight: bold;
        color: var(--accent-blue);
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid var(--border-color);
    }

    /* Data Tables */
    .data-table {
        width: 100%;
        border-collapse: collapse;
        margin: 1rem 0;
        font-size: 10pt;
    }

    .data-table th {
        background: var(--background-subtle);
        padding: 0.4rem;
        text-align: left;
        font-weight: 600;
        border: 1px solid var(--border-color);
    }

    .data-table td {
        padding: 0.4rem;
        border: 1px solid var(--border-color);
        vertical-align: top;
    }

    /* Feature Display */
    .feature-block {
        margin-bottom: 1.5rem;
        break-inside: avoid;
    }

    .feature-header {
        background: var(--background-subtle);
        padding: 0.75rem;
        border: 1px solid var(--border-color);
        margin-bottom: 0.5rem;
    }

    .feature-name {
        font-weight: 600;
        font-size: 11pt;
        margin-bottom: 0.25rem;
    }

    .feature-description {
        color: var(--secondary-text);
        font-size: 10pt;
    }

    .feature-warning {
        margin-top: 0.5rem;
        padding: 0.5rem;
        background: var(--warning-bg);
        color: var(--warning-text);
        border-radius: 3px;
        font-size: 9pt;
    }

    /* Status Badges */
    .status-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.2rem 0.4rem;
        border-radius: 3px;
        font-size: 9pt;
        font-weight: 500;
    }

    .status-required {
        background: var(--required-bg);
        color: var(--required-text);
    }

    .status-nice {
        background: var(--nice-bg);
        color: var(--nice-text);
    }

    .status-not-needed {
        background: var(--not-needed-bg);
        color: var(--not-needed-text);
    }

    .status-need-info {
        background: var(--need-info-bg);
        color: var(--need-info-text);
    }

    /* Questionnaire Styles */
    .questionnaire-section {
        background: white;
        border: 1px solid var(--border-color);
        border-radius: 4px;
        padding: 1rem;
        margin-bottom: 2rem;
    }

    .question-block {
        padding-bottom: 1rem;
        margin-bottom: 1rem;
        border-bottom: 1px solid var(--border-color);
    }

    .question-block:last-child {
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;
    }

    .question-text {
        font-weight: 600;
        margin-bottom: 0.5rem;
    }

    .responses-container {
        margin-left: 1rem;
    }

    /* Statistics Grid */
    .stats-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 1rem;
        margin: 1rem 0;
    }

    .stat-card {
        background: white;
        border: 1px solid var(--border-color);
        border-radius: 4px;
        padding: 1rem;
        break-inside: avoid;
    }

    .stat-number {
        font-size: 18pt;
        font-weight: bold;
        color: var(--accent-blue);
    }

    .stat-label {
        font-size: 9pt;
        color: var(--secondary-text);
        margin-top: 0.25rem;
    }

    /* Print Optimizations */
    @media print {
        body { 
            padding: 0.5rem;
        }

        .page-break {
            page-break-before: always;
        }

        .no-break {
            page-break-inside: avoid;
        }

        .data-table {
            page-break-inside: auto;
        }

        tr {
            page-break-inside: avoid;
        }

        thead {
            display: table-header-group;
        }
    }

    /* Page Headers and Footers */
    @page {
        margin: 2cm;
        
        @top-center {
            content: string(page-title);
            font-size: 9pt;
            color: var(--secondary-text);
        }
        
        @bottom-right {
            content: "Page " counter(page) " of " counter(pages);
            font-size: 9pt;
        }
        
        @bottom-left {
            content: string(doc-title);
            font-size: 9pt;
            color: var(--secondary-text);
        }
    }
`;

// Update the stats grid CSS
const statsGridStyles = `
    .stats-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        gap: 1rem;
        margin: 1rem 0;
    }

    .stat-card {
        background: white;
        border: 1px solid var(--border-color);
        border-radius: 4px;
        padding: 1rem;
        break-inside: avoid;
        min-height: 80px;
        display: flex;
        flex-direction: column;
    }

    .stat-number {
        font-size: 1.5rem;
        font-weight: bold;
        color: var(--accent-blue);
        margin-bottom: 0.25rem;
    }

    .stat-label {
        font-size: 0.8rem;
        color: var(--secondary-text);
        line-height: 1.2;
    }

    .stat-percentage {
        font-size: 0.75rem;
        color: var(--secondary-text);
        margin-top: auto;
    }

    .feature-distribution {
        width: 100%;
        height: 2rem;
        background: var(--background-subtle);
        margin: 1rem 0;
        position: relative;
    }

    .feature-distribution-segment {
        height: 100%;
        position: absolute;
        top: 0;
    }

    .feature-distribution-legend {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
        gap: 0.5rem;
        font-size: 0.8rem;
        margin-top: 0.5rem;
    }
`;


// Helper function to safely get team responses
function getAllTeamResponses(feature, teamResponses = []) {
    if (!feature || !teamResponses || !Array.isArray(teamResponses)) {
        return [];
    }

    return teamResponses
        .filter(response => 
            response && 
            response.feature_reference === feature.reference
        )
        .map(response => ({
            user_name: response.user_name || 'Unknown User',
            user_id: response.user_id,
            response_value: response.response_value,
            created_at: response.created_at
        }));
}

// Feature filtering with validation
function filterFeatures(features, teamResponses, settings) {
    if (!settings?.featureTypes?.length) return features;
    
    return features.filter(feature => {
        const featureResponses = getAllTeamResponses(feature, teamResponses);
        
        // Include unresponded features if setting is enabled
        if (featureResponses.length === 0) {
            return settings.includeUnresponded === true;
        }

        // Check if any response matches allowed types
        return featureResponses.some(response => {
            const responseType = response.response_value;
            const mappedType = responseType === 'dont_know' ? 'need_more_info' : responseType;
            return settings.featureTypes.includes(mappedType);
        });
    });
}

// Format questionnaire responses
function formatQuestionnaireResponse(question, response) {
    if (!response?.response_value) return 'No Response';
    
    try {
        switch (question.question_type) {
            case 'multiple_choice': {
                const choices = typeof response.response_value === 'string' ? 
                    JSON.parse(response.response_value) : 
                    response.response_value;
                return Array.isArray(choices) ? choices.join(', ') : String(response.response_value);
            }
            
            case 'range': {
                try {
                    if (!response.response_value) return 'No Response';
                    
                    // Parse the range value if it's a string
                    let range = response.response_value;
                    if (typeof range === 'string') {
                        try {
                            range = JSON.parse(range);
                        } catch (e) {
                            console.error('Failed to parse range value:', e);
                            return 'Invalid Range Format';
                        }
                    }
                    
                    // Validate the range object
                    if (!range || typeof range !== 'object') {
                        console.warn('Invalid range value:', range);
                        return 'Invalid Range';
                    }
                    
                    // Safely access properties with default values
                    const currency = range.currency || '$';
                    const min = typeof range.min === 'number' ? range.min.toLocaleString() : '0';
                    const max = typeof range.max === 'number' ? range.max.toLocaleString() : '0';
                    
                    return `${currency}${min} - ${currency}${max}`;
                } catch (error) {
                    console.error('Error formatting range value:', error);
                    return 'Invalid Range Format';
                }
            }
            
            case 'tristate_boolean': {
                const valueMap = {
                    'YES': 'Yes',
                    'NO': 'No',
                    'DONT_KNOW': "Don't Know"
                };
                return valueMap[response.response_value] || String(response.response_value);
            }
            
            default:
                return String(response.response_value);
        }
    } catch (error) {
        console.error('Error formatting response:', error);
        return 'Error Formatting Response';
    }
}

// Format response value for display
function formatResponseValue(value) {
    if (!value) return 'No Response';
    
    return value
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

// Generate cover page
function generateCoverPage(title, date, organization, settings) {
    return `
        <div class="cover-page no-break">
            <div class="header">
                <div class="title">${title}</div>
                <div class="org-name">${organization.name}</div>
                <div class="date">Generated on ${date}</div>
                ${organization.website_url ? 
                    `<div class="website">Website: ${organization.website_url}</div>` : 
                    ''}
            </div>

            ${generateConfigurationSummary(settings)}
        </div>
    `;
}

// Generate configuration summary
function generateConfigurationSummary(settings) {
    if (!settings) return '';

    const formatFeatureTypes = (types) => {
        if (!Array.isArray(types) || types.length === 0) {
            return 'All Features';
        }
        return types.map(type => {
            switch(type) {
                case 'required': return 'Required Features';
                case 'nice_to_have': return 'Nice to Have Features';
                case 'not_needed': return 'Not Needed Features';
                case 'need_more_info': return 'Features Needing More Info';
                default: return type;
            }
        }).join(', ');
    };

    return `
        <div class="configuration-summary">
            <h2 class="section-title">Report Configuration</h2>
            <table class="data-table">
                <tbody>
                    <tr>
                        <td class="config-label">Generated On</td>
                        <td>${new Date(settings.generatedAt).toLocaleString()}</td>
                    </tr>
                    <tr>
                        <td class="config-label">Paper Format</td>
                        <td>${settings.paperSize}</td>
                    </tr>
                    <tr>
                        <td class="config-label">Feature Types</td>
                        <td>${formatFeatureTypes(settings.featureTypes)}</td>
                    </tr>
                    <tr>
                        <td class="config-label">Executive Summary</td>
                        <td>${settings.includeAISummary ? 'Included' : 'Not Included'}</td>
                    </tr>
                    <tr>
                        <td class="config-label">Comments</td>
                        <td>${settings.includeComments ? 'Included' : 'Not Included'}</td>
                    </tr>
                    <tr>
                        <td class="config-label">Reactions</td>
                        <td>${settings.includeReactions ? 'Included' : 'Not Included'}</td>
                    </tr>
                    <tr>
                        <td class="config-label">User Details</td>
                        <td>${settings.showUserDetails ? 'Shown' : 'Hidden'}</td>
                    </tr>
                    <tr>
                        <td class="config-label">Unresponded Features</td>
                        <td>${settings.includeUnresponded ? 'Listed in Appendix' : 'Not Included'}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    `;
}
// Generate feature content
function generateFeatureRow(feature, responses, teamResponses = [], settings = {}) {
    const statusClassMap = {
        'required': 'bg-green-100 text-green-800',
        'nice_to_have': 'bg-blue-100 text-blue-800',
        'not_needed': 'bg-red-100 text-red-800',
        'need_more_info': 'bg-yellow-100 text-yellow-800'
    };

    // Get all responses for this feature
    const featureResponses = teamResponses.filter(response => 
        response.feature_reference === feature.reference
    );

    return `
        <div class="feature-row mb-6">
            <table class="feature-table w-full border-collapse">
                <tr>
                    <td class="w-1/4 p-4 border-r">
                        <h3 class="text-lg font-semibold text-gray-900">${feature.name}</h3>
                        ${feature.reference ? `
                            <div class="text-sm text-gray-500 mt-1">Reference: ${feature.reference}</div>
                        ` : ''}
                    </td>
                    <td class="w-1/2 p-4 border-r">
                        <p class="text-gray-600">${feature.description}</p>
                        
                        ${feature.comments && feature.comments.length > 0 && settings.includeComments ? `
                            <div class="mt-4 pt-4 border-t">
                                <h4 class="text-sm font-medium text-gray-700 mb-2">Comments & Discussion</h4>
                                <div class="space-y-3">
                                    ${generateCommentsSection(feature, settings)}
                                </div>
                            </div>
                        ` : ''}
                    </td>
                    <td class="w-1/4 p-4">
                        <div class="mb-4">
                            <h4 class="text-sm font-medium text-gray-700 mb-2">Team Responses</h4>
                            <div class="space-y-2">
                                ${generateTeamResponsesSection(featureResponses, statusClassMap)}
                            </div>
                        </div>
                        
                        <div>
                            <h4 class="text-sm font-medium text-gray-700 mb-2">Dependencies</h4>
                            ${generateDependenciesSection(feature)}
                        </div>
                    </td>
                </tr>
            </table>
        </div>
    `;
}

function generateTeamResponsesSection(responses, statusClassMap) {
    // Ensure responses is an array
    const responseArray = Array.isArray(responses) ? responses : [];
    
    if (responseArray.length === 0) {
        return '<div class="text-gray-500">No responses yet</div>';
    }

    return responseArray
        .filter(response => response && response.user_name && response.response_value)
        .map(response => `
            <div class="flex items-center justify-between">
                <span class="text-sm font-medium">${response.user_name}</span>
                <span class="px-2 py-1 text-xs font-medium rounded-full ${statusClassMap[response.response_value] || 'bg-gray-100 text-gray-800'}">
                    ${formatResponseValue(response.response_value)}
                </span>
            </div>
        `).join('\n');
}

function generateDependenciesSection(feature) {
    if (!feature.dependencies || feature.dependencies.length === 0) {
        return '<div class="text-gray-500">No dependencies</div>';
    }

    return `
        <ul class="text-sm space-y-2">
            ${feature.dependencies.map(dep => `
                <li class="flex items-start">
                    <span class="inline-block w-2 h-2 mt-1.5 mr-2 bg-gray-400 rounded-full"></span>
                    <span class="text-gray-700">${dep.name}</span>
                </li>
            `).join('\n')}
        </ul>
    `;
}

function generateCommentsSection(feature, settings) {
    if (!feature.comments || feature.comments.length === 0) {
        return '<div class="text-gray-500">No comments yet</div>';
    }

    return feature.comments.map(comment => `
        <div class="comment-block border-l-4 border-gray-200 pl-3">
            <div class="flex items-center justify-between mb-1">
                <span class="font-medium text-gray-900">${comment.user_name}</span>
                <span class="text-xs text-gray-500">${new Date(comment.created_at).toLocaleDateString()}</span>
            </div>
            <div class="text-gray-700">${comment.comment_text}</div>
            ${settings.includeReactions && comment.reactions ? `
                <div class="mt-1">
                    ${generateReactionsList(comment.reactions)}
                </div>
            ` : ''}
        </div>
    `).join('\n');
}

// Generate reactions list
function generateReactionsList(reactions) {
    const groupedReactions = reactions.reduce((acc, reaction) => {
        acc[reaction.emoji] = acc[reaction.emoji] || 0;
        acc[reaction.emoji]++;
        return acc;
    }, {});

    return Object.entries(groupedReactions)
        .map(([emoji, count]) => `
            <span class="reaction-item">
                ${emoji} ${count}
            </span>
        `).join('');
}

// Generate questionnaire section
function generateQuestionnaireSection(questions, orgResponses, userResponses) {
    if (!questions || questions.length === 0) return '';

    return `
        <div class="questionnaire-section">
            <h2 class="section-title">Requirements Questionnaire</h2>
            <div class="questionnaire-content">
                ${questions.map(question => {
                    const orgResponse = orgResponses[question.id];
                    const teamResponses = userResponses.filter(r => r.question_id === question.id);
                    return generateQuestionBlock(question, orgResponse, teamResponses);
                }).join('\n')}
            </div>
        </div>
    `;
}

function generateQuestionBlock(question, orgResponse, teamResponses) {
    return `
        <div class="question-block mb-8 p-6 bg-white rounded-lg shadow">
            <h3 class="text-xl font-semibold text-gray-900 mb-4">${question.question_text}</h3>
            
            <div class="response-section">
                <div class="mb-4">
                    <h4 class="text-md font-medium text-gray-700 mb-2">Organization Response:</h4>
                    <div class="pl-4 py-2 border-l-4 border-blue-500 bg-blue-50">
                        ${formatQuestionnaireResponse(question, orgResponse)}
                    </div>
                </div>

                ${teamResponses.length > 0 ? `
                    <div>
                        <h4 class="text-md font-medium text-gray-700 mb-2">Team Responses:</h4>
                        <div class="space-y-2">
                            ${teamResponses.map(response => `
                                <div class="pl-4 py-2 border-l-4 border-green-500 bg-green-50">
                                    <div class="font-medium text-gray-900">${response.user_name}:</div>
                                    <div class="text-gray-700">${formatQuestionnaireResponse(question, response)}</div>
                                </div>
                            `).join('\n')}
                        </div>
                    </div>
                ` : ''}
            </div>
        </div>
    `;
}
// Generate statistics section
function generateStatisticsSection(statistics, teamResponses) {
    const teamStats = calculateTeamStats(teamResponses);
    const featureStats = calculateFeatureStats(statistics);

    return `
        <div class="statistics-section page-break">
            <h2 class="section-title">Project Statistics</h2>
            
            <div class="stats-container">
                ${generateFeatureStatsGrid(featureStats)}
                ${generateTeamStatsGrid(teamStats)}
                ${generateProgressChart(statistics)}
            </div>
        </div>
    `;
}

// Calculate team statistics
function calculateTeamStats(teamResponses) {
    const uniqueUsers = new Set(teamResponses.map(r => r.user_id));
    const responseTypes = teamResponses.reduce((acc, response) => {
        acc[response.response_value] = (acc[response.response_value] || 0) + 1;
        return acc;
    }, {});

    const percentages = Object.entries(responseTypes).reduce((acc, [type, count]) => {
        acc[type] = ((count / teamResponses.length) * 100).toFixed(1);
        return acc;
    }, {});

    return {
        totalResponses: teamResponses.length,
        uniqueUsers: uniqueUsers.size,
        responseTypes,
        percentages,
        averageResponsesPerUser: (teamResponses.length / uniqueUsers.size).toFixed(1)
    };
}

// Calculate feature statistics
function calculateFeatureStats(statistics) {
    const total = statistics.totalFeatures || 0;
    const percentages = {
        required: ((statistics.requiredFeatures / total) * 100).toFixed(1),
        niceToHave: ((statistics.niceToHaveFeatures / total) * 100).toFixed(1),
        notNeeded: ((statistics.notNeededFeatures / total) * 100).toFixed(1),
        needsInfo: ((statistics.dontKnowFeatures / total) * 100).toFixed(1),
        undecided: ((statistics.undecidedFeatures / total) * 100).toFixed(1)
    };

    return {
        total,
        required: statistics.requiredFeatures || 0,
        niceToHave: statistics.niceToHaveFeatures || 0,
        notNeeded: statistics.notNeededFeatures || 0,
        needsInfo: statistics.dontKnowFeatures || 0,
        undecided: statistics.undecidedFeatures || 0,
        percentages
    };
}

// Generate feature statistics grid
function generateFeatureStatsGrid(stats) {
    return `
        <div class="stats-grid feature-stats">
            <div class="stat-card total-features">
                <div class="stat-number">${stats.total}</div>
                <div class="stat-label">Total Features</div>
            </div>

            <div class="stat-card required-features">
                <div class="stat-number">${stats.required}</div>
                <div class="stat-label">Required (${stats.percentages.required}%)</div>
            </div>

            <div class="stat-card nice-features">
                <div class="stat-number">${stats.niceToHave}</div>
                <div class="stat-label">Nice to Have (${stats.percentages.niceToHave}%)</div>
            </div>

            <div class="stat-card not-needed-features">
                <div class="stat-number">${stats.notNeeded}</div>
                <div class="stat-label">Not Needed (${stats.percentages.notNeeded}%)</div>
            </div>

            <div class="stat-card needs-info-features">
                <div class="stat-number">${stats.needsInfo}</div>
                <div class="stat-label">Needs Info (${stats.percentages.needsInfo}%)</div>
            </div>

            <div class="stat-card undecided-features">
                <div class="stat-number">${stats.undecided}</div>
                <div class="stat-label">Undecided (${stats.percentages.undecided}%)</div>
            </div>
        </div>
    `;
}

// Generate team statistics grid
function generateTeamStatsGrid(stats) {
    return `
        <div class="stats-grid team-stats">
            <div class="stat-card total-responses">
                <div class="stat-number">${stats.totalResponses}</div>
                <div class="stat-label">Total Responses</div>
            </div>

            <div class="stat-card unique-users">
                <div class="stat-number">${stats.uniqueUsers}</div>
                <div class="stat-label">Contributing Users</div>
            </div>

            <div class="stat-card avg-responses">
                <div class="stat-number">${stats.averageResponsesPerUser}</div>
                <div class="stat-label">Avg. Responses per User</div>
            </div>

            ${Object.entries(stats.responseTypes).map(([type, count]) => `
                <div class="stat-card response-type">
                    <div class="stat-number">${count}</div>
                    <div class="stat-label">
                        ${formatResponseValue(type)}
                        (${stats.percentages[type]}%)
                    </div>
                </div>
            `).join('')}
        </div>
    `;
}

// Generate progress chart
function generateProgressChart(statistics) {
    const total = statistics.totalFeatures;
    const categories = [
        { name: 'Required', count: statistics.requiredFeatures, color: '#166534' },
        { name: 'Nice to Have', count: statistics.niceToHaveFeatures, color: '#1e40af' },
        { name: 'Not Needed', count: statistics.notNeededFeatures, color: '#374151' },
        { name: 'Needs Info', count: statistics.dontKnowFeatures, color: '#92400e' },
        { name: 'Undecided', count: statistics.undecidedFeatures, color: '#6b7280' }
    ];

    let cumulative = 0;
    const segments = categories.map(cat => {
        const percentage = (cat.count / total) * 100;
        const segment = {
            start: cumulative,
            end: cumulative + percentage,
            ...cat
        };
        cumulative += percentage;
        return segment;
    });

    return `
        <div class="progress-chart no-break">
            <h3 class="chart-title">Feature Response Distribution</h3>
            <div class="chart-container">
                <svg viewBox="0 0 400 120" class="w-full">
                    ${segments.map(segment => {
                        const width = (segment.end - segment.start) * 4;
                        // Only show text if segment is wide enough
                        const showText = width > 8;
                        return `
                            <g>
                                <rect
                                    x="${segment.start * 4}%"
                                    y="0"
                                    width="${width}%"
                                    height="40"
                                    fill="${segment.color}"
                                />
                                ${showText ? `
                                    <text
                                        x="${(segment.start + (segment.end - segment.start) / 2) * 4}%"
                                        y="60"
                                        text-anchor="middle"
                                        fill="#1f2937"
                                        font-size="12"
                                        font-family="Arial, sans-serif"
                                    >
                                        ${segment.name}
                                    </text>
                                    <text
                                        x="${(segment.start + (segment.end - segment.start) / 2) * 4}%"
                                        y="80"
                                        text-anchor="middle"
                                        fill="#4b5563"
                                        font-size="10"
                                        font-family="Arial, sans-serif"
                                    >
                                        ${segment.count} (${((segment.end - segment.start)).toFixed(1)}%)
                                    </text>
                                ` : ''}
                            </g>
                        `;
                    }).join('')}
                </svg>
            </div>
            <div class="feature-distribution-legend" style="margin-top: 1rem; display: flex; flex-wrap: wrap; gap: 1rem; justify-content: center;">
                ${segments.map(segment => `
                    <div style="display: flex; align-items: center; gap: 0.5rem;">
                        <div style="width: 12px; height: 12px; background-color: ${segment.color};"></div>
                        <span style="font-size: 0.875rem; color: #374151;">
                            ${segment.name}: ${segment.count} (${((segment.end - segment.start)).toFixed(1)}%)
                        </span>
                    </div>
                `).join('')}
            </div>
        </div>
    `;
}

// Generate unresponded features section
function generateUnrespondedFeaturesSection(features, responses) {
    const unrespondedFeatures = features.filter(feature => 
        !Object.values(responses).some(response => 
            response.feature_reference === feature.reference
        )
    );

    if (!unrespondedFeatures.length) return '';

    return `
        <div class="section unresponded-features page-break">
            <h2 class="section-title">Appendix: Unresponded Features</h2>
            <p class="section-description">
                The following features have not received any team responses as of report generation.
            </p>

            <table class="data-table">
                <thead>
                    <tr>
                        <th>Reference</th>
                        <th>Section</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Dependencies</th>
                    </tr>
                </thead>
                <tbody>
                    ${unrespondedFeatures.map(feature => `
                        <tr>
                            <td>${feature.reference}</td>
                            <td>${feature.section}</td>
                            <td>${feature.name}</td>
                            <td>${feature.description || 'No description provided'}</td>
                            <td>
                                ${feature.dependencies?.length ? 
                                    feature.dependencies.map(dep => 
                                        `• ${dep.name}${dep.is_system_dependency ? ' (System)' : ''}`
                                    ).join('<br>') : 
                                    'None'
                                }
                            </td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
        </div>
    `;
}

// Generate dependencies appendix
function generateDependenciesAppendix(features) {
    const dependencyMap = features.reduce((acc, feature) => {
        if (feature.dependencies?.length) {
            acc[feature.reference] = {
                name: feature.name,
                dependencies: feature.dependencies
            };
        }
        return acc;
    }, {});

    if (Object.keys(dependencyMap).length === 0) return '';

    return `
        <div class="section dependencies-appendix page-break">
            <h2 class="section-title">Appendix: Feature Dependencies</h2>
            <p class="section-description">
                This section lists all features with dependencies and their relationships.
            </p>

            <table class="data-table">
                <thead>
                    <tr>
                        <th>Feature</th>
                        <th>Required Dependencies</th>
                        <th>System Dependencies</th>
                    </tr>
                </thead>
                <tbody>
                    ${Object.entries(dependencyMap).map(([ref, info]) => {
                        const regularDeps = info.dependencies.filter(d => !d.is_system_dependency);
                        const systemDeps = info.dependencies.filter(d => d.is_system_dependency);
                        
                        return `
                            <tr>
                                <td>
                                    <div class="feature-ref">${ref}</div>
                                    <div class="feature-name">${info.name}</div>
                                </td>
                                <td>
                                    ${regularDeps.length ? 
                                        regularDeps.map(dep => 
                                            `<div class="dependency-item">• ${dep.name}</div>`
                                        ).join('') : 
                                        'None'
                                    }
                                </td>
                                <td>
                                    ${systemDeps.length ? 
                                        systemDeps.map(dep => 
                                            `<div class="dependency-item">• ${dep.name}</div>`
                                        ).join('') : 
                                        'None'
                                    }
                                </td>
                            </tr>
                        `;
                    }).join('')}
                </tbody>
            </table>
        </div>
    `;
}

// Generate comments appendix
function generateCommentsAppendix(features, settings) {
    if (!settings.includeComments) return '';

    const featuresWithComments = features.filter(f => f.comments?.length > 0);
    if (featuresWithComments.length === 0) return '';

    return `
        <div class="section comments-appendix page-break">
            <h2 class="section-title">Appendix: Feature Comments</h2>
            <p class="section-description">
                Comprehensive list of all comments and discussions on features.
            </p>

            ${featuresWithComments.map(feature => `
                <div class="feature-comments no-break">
                    <h3 class="feature-title">
                        [${feature.reference}] ${feature.name}
                    </h3>
                    <div class="comments-container">
                        ${feature.comments.map(comment => `
                            <div class="comment-item">
                                <div class="comment-header">
                                    <strong>${comment.user_name}</strong>
                                    <span class="comment-date">
                                        ${new Date(comment.created_at).toLocaleString()}
                                    </span>
                                </div>
                                <div class="comment-content">
                                    ${comment.comment_text}
                                </div>
                                ${settings.includeReactions && comment.reactions?.length ? `
                                    <div class="reactions-list">
                                        ${generateReactionsList(comment.reactions)}
                                    </div>
                                ` : ''}
                            </div>
                        `).join('')}
                    </div>
                </div>
            `).join('')}
        </div>
    `;
}
// Main report generation function
export const generateReportHTML = async (data) => {
    if (!data || typeof data !== 'object') {
        throw new Error('Invalid report data provided');
    }

    const {
        title,
        date,
        organization,
        features,
        responses,
        teamResponses = [],
        statistics,
        includeQuestionnaire = true,
        questions = [],
        orgResponses = {},
        userResponses = [],
        settings = {}
    } = data;

    // Apply paper size setting
    const paperSize = settings.paperSize || 'A4';
    const paperStyles = `
        @page {
            size: ${paperSize} portrait;
            margin: 2cm;
            @bottom-right {
                content: "Page " counter(page) " of " counter(pages);
            }
            @bottom-left {
                content: "Generated: ${date}";
            }
            @top-center {
                content: string(page-title);
            }
        }
    `;

    const combinedStyles = `
        ${baseStyles}
        ${paperStyles}
    `;

    const filteredFeatures = filterFeatures(features, teamResponses, settings);

    // Generate AI summary if enabled
    let aiSummaryContent = '';
    if (settings.includeAISummary !== false) {
        try {
            const aiSummary = await generateRequirementsSummary({
                features,
                responses,
                organization,
                questions,
                orgResponses,
                userResponses
            });
            aiSummaryContent = `
                <div class="section executive-summary">
                    <h2 class="section-title">Executive Summary</h2>
                    <div class="summary-content">
                        ${aiSummary.split('\n\n').map(p => `<p>${p}</p>`).join('')}
                    </div>
                </div>
            `;
        } catch (error) {
            console.error('Failed to generate AI summary:', error);
            aiSummaryContent = `
                <div class="section executive-summary">
                    <h2 class="section-title">Executive Summary</h2>
                    <div class="summary-content">
                        <p class="error-message">
                            Unable to generate executive summary. Proceeding with report generation.
                        </p>
                    </div>
                </div>
            `;
        }
    }

    // Group features by section
    const featuresBySection = filteredFeatures.reduce((acc, feature) => {
        if (!acc[feature.section]) {
            acc[feature.section] = [];
        }
        acc[feature.section].push(feature);
        return acc;
    }, {});

    // Generate features content
    const featuresContent = Object.entries(featuresBySection)
        .map(([section, sectionFeatures]) => `
            <div class="section features-section">
                <h2 class="section-title">
                    ${section}
                    <span class="feature-count">
                        (${sectionFeatures.length} feature${sectionFeatures.length !== 1 ? 's' : ''})
                    </span>
                </h2>
                <div class="features-container">
                    ${sectionFeatures.map(feature => 
                        generateFeatureRow(feature, responses, teamResponses, settings)
                    ).join('')}
                </div>
            </div>
        `)
        .join('');

    // Assemble the complete report
    return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${title}</title>
            <style>
                ${combinedStyles}
            </style>
        </head>
        <body>
            ${generateCoverPage(title, date, organization, settings)}

            <div class="report-content">
                ${settings.includeAISummary && aiSummaryContent ? aiSummaryContent : ''}

                ${generateStatisticsSection(statistics, teamResponses)}

                ${includeQuestionnaire ? 
                    generateQuestionnaireSection(questions, orgResponses, userResponses) : 
                    ''}

                ${featuresContent}

                ${settings.includeUnresponded ? 
                    generateUnrespondedFeaturesSection(features, responses) : 
                    ''}

                ${generateDependenciesAppendix(features)}

                ${settings.includeComments ? 
                    generateCommentsAppendix(features, settings) : 
                    ''}
            </div>

            <div class="report-footer">
                <div class="footer-content">
                    <div>Generated for ${organization.name}</div>
                    <div>Report Date: ${date}</div>
                    ${organization.website_url ? 
                        `<div>Website: ${organization.website_url}</div>` : 
                        ''}
                </div>
            </div>
        </body>
        </html>
    `;
};

// Utility function for testing
export function validateReportData(data) {
    const requiredFields = ['title', 'date', 'organization', 'features', 'responses'];
    const missingFields = requiredFields.filter(field => !data[field]);
    
    if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    if (!Array.isArray(data.features)) {
        throw new Error('Features must be an array');
    }

    if (typeof data.responses !== 'object') {
        throw new Error('Responses must be an object');
    }

    return true;
}

// Export all functions for testing and reuse
export default {
    generateReportHTML,
    filterFeatures,
    generateFeatureRow,
    generateQuestionnaireSection,
    generateStatisticsSection,
    getAllTeamResponses,
    formatQuestionnaireResponse,
    validateReportData,
    generateCoverPage,
    generateUnrespondedFeaturesSection,
    generateDependenciesAppendix,
    generateCommentsAppendix,
    formatResponseValue
};